import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Phone, Users, TrendingUp, Clock, CheckCircle, XCircle, History, PhoneCall } from 'lucide-react';
import { TelecallerService } from '../../services/telecallerService';

const TelecallerDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [showCallLogs, setShowCallLogs] = useState(false);

  const { data: statsData, isLoading } = useQuery({
    queryKey: ['telecallerStats', user?.id],
    queryFn: () => TelecallerService.getTelecallerStats(user?.id),
    enabled: !!user?.id,
  });

  const { data: callLogsData } = useQuery({
    queryKey: ['telecallerCallLogs', user?.id],
    queryFn: () => TelecallerService.getCallLogs(user?.id),
    enabled: !!user?.id && showCallLogs,
  });

  const stats = statsData?.data || {
    assignedLeads: 0,
    calledToday: 0,
    confirmed: 0,
    booked: 0,
    conversionRate: 0,
    pendingCalls: 0,
    dialerLeads: 0,
    callbackRate: 0,
    noAnswerRate: 0,
    averageCallDuration: 0,
  };

  const callLogs = callLogsData?.data || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Telecaller Dashboard</h1>
          <p className="text-white/80">Manage your leads and track performance</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">Telecaller Dashboard</h1>
        <p className="text-white/80">Manage your leads and track performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Assigned Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats.assignedLeads || stats.totalLeads || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Called Today</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedCalls || stats.calledToday || 0}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Phone size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Confirmed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.convertedLeads || stats.confirmed || 0}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={24} className="text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Booked</p>
              <p className="text-2xl font-bold text-gray-900">{stats.deliveredLeads || stats.booked || 0}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={24} className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Calls</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingCalls || 0}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock size={24} className="text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.conversionRate ? stats.conversionRate.toFixed(1) : '0.0'}%
              </p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <TrendingUp size={24} className="text-indigo-600" />
            </div>
          </div>
        </div>

        {stats.callbackRate !== undefined && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Callback Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.callbackRate ? stats.callbackRate.toFixed(1) : '0.0'}%
                </p>
              </div>
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <PhoneCall size={24} className="text-cyan-600" />
              </div>
            </div>
          </div>
        )}

        {stats.noAnswerRate !== undefined && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">No Answer Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.noAnswerRate ? stats.noAnswerRate.toFixed(1) : '0.0'}%
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle size={24} className="text-red-600" />
              </div>
            </div>
          </div>
        )}

        {stats.averageCallDuration !== undefined && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Avg Call Duration</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.averageCallDuration ? `${Math.round(stats.averageCallDuration / 60)}m` : '0m'}
                </p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <Clock size={24} className="text-teal-600" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Quick Actions</h2>
          <button
            onClick={() => setShowCallLogs(!showCallLogs)}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center space-x-2"
          >
            <History size={16} />
            <span>{showCallLogs ? 'Hide' : 'Show'} Call Logs</span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/telecaller/dialer')}
            className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Start Dialing
          </button>
          <button 
            onClick={() => navigate('/telecaller/assigned-leads')}
            className="px-4 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
          >
            Assigned Leads
          </button>
          <button 
            onClick={() => navigate('/telecaller/leads-history')}
            className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            View Leads History
          </button>
          <button 
            onClick={() => navigate('/telecaller/not-answer')}
            className="px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
          >
            Not Answer Leads
          </button>
          <button 
            onClick={() => navigate('/telecaller/not-interested')}
            className="px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            Not Interested Leads
          </button>
          <button 
            onClick={() => navigate('/telecaller/booked')}
            className="px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            View Booked Orders
          </button>
        </div>
      </div>

      {showCallLogs && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Call Logs</h2>
          {callLogs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No call logs yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {callLogs.slice(0, 10).map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.call_time ? new Date(log.call_time).toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                          {log.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.duration_seconds ? `${Math.round(log.duration_seconds / 60)}m` : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.notes ? (log.notes.length > 50 ? log.notes.substring(0, 50) + '...' : log.notes) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TelecallerDashboard;

